<template>
  <div class="policy-consent">
    <p class="consent-intro">กรุณากดอ่านและรับทราบเอกสารต่อไปนี้ให้ครบทุกฉบับ ก่อนยอมรับข้อตกลง</p>

    <ul class="policy-items">
      <li
        v-for="p in policies"
        :key="p.type"
        class="policy-item"
        :class="{ 'is-viewed': viewed[p.type] }"
        @click="openPolicy(p)"
      >
        <div class="policy-item-left">
          <v-icon :color="viewed[p.type] ? '#16a34a' : '#9ca3af'" size="22">
            {{ viewed[p.type] ? "mdi-check-circle" : "mdi-circle-outline" }}
          </v-icon>
          <span class="policy-item-text">{{ p.label }}</span>
        </div>
        <span class="read-btn">
          {{ viewed[p.type] ? "อ่านอีกครั้ง" : "กดอ่าน" }}
          <v-icon size="16">mdi-chevron-right</v-icon>
        </span>
      </li>
    </ul>

    <div class="checkbox-wrap">
      <v-checkbox
        v-model="accepted"
        :disabled="!allViewed"
        :ripple="false"
        hide-details
        class="mt-0 pt-0"
      >
        <template #label>
          <span class="checkbox-label">ฉันได้อ่านและยอมรับเอกสารทั้งหมดข้างต้น</span>
        </template>
      </v-checkbox>
      <p v-if="!allViewed" class="hint-text">* กรุณากดอ่านเอกสารให้ครบทุกฉบับก่อน</p>
    </div>

    <!-- Reader Dialog -->
    <v-dialog v-model="dialog" max-width="780" scrollable>
      <v-card class="policy-dialog-card">
        <v-card-title class="dialog-title">
          <span class="dialog-title-text">{{ current.label }}</span>
          <v-spacer />
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="dialog-content">
          <div v-if="loading" class="d-flex justify-center py-12">
            <v-progress-circular indeterminate color="#F4811F" />
          </div>
          <div v-else-if="error" class="text-center py-12">
            <p class="grey--text mb-3">{{ error }}</p>
            <v-btn small outlined color="#F4811F" @click="fetchContent(current.type)">ลองใหม่</v-btn>
          </div>
          <div v-else>
            <p v-if="meta.effectiveDate" class="dialog-date">
              วันที่เริ่มใช้บังคับ: {{ formatDate(meta.effectiveDate) }}
            </p>
            <div class="policy-text">{{ content }}</div>
          </div>
        </v-card-text>

        <v-divider />
        <v-card-actions class="dialog-actions">
          <v-spacer />
          <v-btn text @click="dialog = false">ปิด</v-btn>
          <v-btn class="ack-btn" :disabled="loading || !!error" @click="acknowledge">
            <v-icon left size="18">mdi-check</v-icon>
            รับทราบ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getPolicyByTypeApi } from "@/services/api/policy";

export default {
  name: "PolicyConsent",

  props: {
    value: { type: Boolean, default: false },
  },

  data() {
    return {
      dialog: false,
      loading: false,
      error: null,
      content: "",
      meta: {},
      current: {},
      cache: {},
      viewed: { terms: false, privacy: false, payment: false },
      policies: [
        { type: "terms", label: "ข้อกำหนดการใช้งาน" },
        { type: "privacy", label: "ประกาศความเป็นส่วนตัว" },
        { type: "payment", label: "ข้อตกลงการชำระเงิน" },
      ],
    };
  },

  computed: {
    allViewed() {
      return this.policies.every((p) => this.viewed[p.type]);
    },
    accepted: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      },
    },
  },

  methods: {
    openPolicy(p) {
      this.current = p;
      this.dialog = true;
      this.fetchContent(p.type);
    },

    async fetchContent(type) {
      if (this.cache[type]) {
        this.content = this.cache[type].content;
        this.meta = this.cache[type].meta;
        this.error = null;
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        const res = await getPolicyByTypeApi(type);
        const data = res.data.data;
        this.content = data.content;
        this.meta = { effectiveDate: data.effectiveDate, title: data.title };
        this.cache[type] = { content: this.content, meta: this.meta };
      } catch (err) {
        this.error = "ไม่สามารถโหลดเอกสารได้ กรุณาลองใหม่อีกครั้ง";
      } finally {
        this.loading = false;
      }
    },

    acknowledge() {
      this.$set(this.viewed, this.current.type, true);
      this.dialog = false;
    },

    formatDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString("th-TH", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
  },
};
</script>

<style scoped>
.policy-consent {
  font-family: "Noto Sans Thai", system-ui, Arial, sans-serif;
}
.consent-intro {
  font-size: 14px;
  color: #4b5563;
  margin-bottom: 12px;
}
.policy-items {
  list-style: none;
  padding: 0;
  margin: 0 0 16px;
}
.policy-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: border-color 0.18s, background 0.18s;
}
.policy-item:hover {
  border-color: #F4811F;
  background: #fff7ed;
}
.policy-item.is-viewed {
  border-color: #bbf7d0;
  background: #f0fdf4;
}
.policy-item-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}
.policy-item-text {
  font-size: 15px;
  font-weight: 500;
  color: #1f2937;
  line-height: 1.4;
}
.read-btn {
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #F4811F;
  white-space: nowrap;
  flex-shrink: 0;
}
.checkbox-wrap {
  margin-top: 4px;
}
.checkbox-label {
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
}
.hint-text {
  font-size: 12px;
  color: #9ca3af;
  margin: 4px 0 0 32px;
}

/* Dialog */
.dialog-title {
  display: flex;
  align-items: center;
  font-size: 17px;
  font-weight: 700;
  color: #111827;
  font-family: "Noto Sans Thai", "Kanit", sans-serif;
  padding: 16px 20px;
}
.dialog-title-text {
  word-break: break-word;
}
.dialog-content {
  max-height: 60vh;
  padding: 20px;
}
.dialog-date {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 12px;
}
.policy-text {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 14px;
  line-height: 1.9;
  color: #1f2937;
  text-align: justify;
}
.dialog-actions {
  padding: 12px 20px;
}
.ack-btn {
  background: #F4811F !important;
  color: #fff !important;
  border-radius: 8px !important;
  font-weight: 600;
}

/* Mobile: ขยายระยะแตะและเน้นปุ่มให้อ่านง่ายขึ้น */
@media (max-width: 600px) {
  .policy-item {
    padding: 14px;
    gap: 10px;
  }
  .policy-item-text {
    font-size: 15.5px;
    line-height: 1.45;
  }
  .read-btn {
    font-size: 13.5px;
    padding: 5px 10px;
    border-radius: 999px;
    background: #fff7ed;
    border: 1px solid #fed7aa;
  }
  .policy-item.is-viewed .read-btn {
    background: #f0fdf4;
    border-color: #bbf7d0;
    color: #16a34a;
  }
  .hint-text {
    margin-left: 0;
  }
}
</style>
